import { AppearanceError } from './errors.js';
import { contrastRatio, MIN_CONTRAST, normaliseHex, ratioText } from './contrast.js';
import { composite } from './tokens.js';
import type { PassColours } from './theme.js';

/*
 * Card designs (CLAUDE.md §4 "Card designs", Saif 24 Oct 2026; docs/26 §5.2): the Tikkas card on Home, the join page
 * and the Wallet pass wear one of a list of designs the admin keeps, and rules say who gets which: "a tag gets a colour".
 * A design is two or three gradient stops, the ink (text colour), a luminous or dark 1px rim, the sheen on or off and an
 * optional subtle pattern. The guard rail: the ink keeps 4.5:1 against every stop, or the admin refuses it, so the balance
 * numbers and the name always read. A customer's tags are the group (founder, creator, staff), the level's id (regular,
 * gold) and anything staff typed on their card; the first enabled rule whose tag they carry wins, else the default design.
 * Stored as site_settings row `app_card_designs`; GET /api/appearance carries the default design (`cardDesign`) and
 * GET /api/me the customer's own (`card.design`).
 */

export const CARD_PATTERNS = ['none', 'grain', 'brushed', 'guilloche', 'glow', 'geometric', 'watermark'] as const;
export type CardPattern = (typeof CARD_PATTERNS)[number];
export const CARD_PATTERN_NAMES: Record<CardPattern, { label: string; sub: string }> = {
  none: { label: 'Plain', sub: 'The colours alone' },
  grain: { label: 'Fine grain', sub: 'A faint speckle, like card stock' },
  brushed: { label: 'Brushed metal', sub: 'Fine diagonal lines' },
  guilloche: { label: 'Guilloché', sub: 'Fine engraved rings, like a banknote' },
  glow: { label: 'Soft glow', sub: 'A light pool in one corner, shade in the other' },
  geometric: { label: 'Geometric', sub: 'A faint diamond lattice' },
  watermark: { label: 'Skewer watermark', sub: 'A faint embossed Tikkas skewer' },
};
/** Patterns busy enough that the balance block gets a faint backplate, so the numbers always read. */
export const BUSY_PATTERNS: readonly CardPattern[] = ['guilloche', 'geometric'];

export const CARD_FINISHES = ['matte', 'satin', 'gloss', 'metallic'] as const;
export type CardFinish = (typeof CARD_FINISHES)[number];
export const CARD_FINISH_NAMES: Record<CardFinish, { label: string; sub: string }> = {
  matte: { label: 'Matte', sub: 'Flat, no highlight' },
  satin: { label: 'Satin', sub: 'A soft corner highlight' },
  gloss: { label: 'Gloss', sub: 'A bright specular band' },
  metallic: { label: 'Metallic', sub: 'Fine light-and-dark banding, like metal' },
};

export const CARD_RIMS = ['none', 'hairline', 'gold', 'double'] as const;
export type CardRim = (typeof CARD_RIMS)[number];
export const CARD_RIM_NAMES: Record<CardRim, { label: string; sub: string }> = {
  none: { label: 'No rim', sub: 'The colours run to the edge' },
  hairline: { label: 'Hairline', sub: 'A fine edge, light on deep colours and dark on pale ones' },
  gold: { label: 'Luminous gold hairline', sub: 'A fine gold edge with a faint inner glow' },
  double: { label: 'Double hairline', sub: 'The edge and a second fine line just inside it' },
};

export const CARD_LOGOS = ['ink', 'white', 'gold', 'debossed'] as const;
export type CardLogo = (typeof CARD_LOGOS)[number];
export const CARD_LOGO_NAMES: Record<CardLogo, { label: string; sub: string }> = {
  ink: { label: 'Ink', sub: 'The logo and badge in the text colour' },
  white: { label: 'White', sub: 'A white logo and badge' },
  gold: { label: 'Gold', sub: 'A gold logo and badge' },
  debossed: { label: 'Debossed', sub: 'Pressed into the card, in the text colour' },
};
/** The gold the rim, the logo treatment and the flecks share. */
export const CARD_GOLD = '#e9c97a';

export const CARD_ANIMATIONS = ['none', 'sheen', 'holographic', 'flecks', 'breathe', 'liquid', 'twinkle', 'parallax'] as const;
export type CardAnimation = (typeof CARD_ANIMATIONS)[number];
export const CARD_ANIMATION_NAMES: Record<CardAnimation, { label: string; sub: string }> = {
  none: { label: 'Still', sub: 'Nothing moves' },
  sheen: { label: 'Sheen pass', sub: 'A light sweeps across every 14 seconds' },
  holographic: { label: 'Holographic drift', sub: 'A faint rainbow that drifts, and follows the phone’s tilt' },
  flecks: { label: 'Gold flecks', sub: 'Tiny gold flecks shimmer slowly' },
  breathe: { label: 'Breathing glow', sub: 'The edges glow in and out' },
  liquid: { label: 'Liquid metal', sub: 'Light and shade flow slowly across the card' },
  twinkle: { label: 'Twinkle', sub: 'Tiny points of light come and go' },
  parallax: { label: 'Parallax', sub: 'The pattern shifts against the colours with the tilt' },
};
export const CARD_INTENSITIES = ['subtle', 'normal', 'bold'] as const;
export type CardIntensity = (typeof CARD_INTENSITIES)[number];
export const CARD_INTENSITY_NAMES: Record<CardIntensity, string> = { subtle: 'Subtle', normal: 'Normal', bold: 'Bold' };
const INTENSITY_SCALE: Record<CardIntensity, number> = { subtle: 0.6, normal: 1, bold: 1.4 };

export const CARD_DESIGN_NAME_MAX = 30;
export const CARD_DESIGN_DESCRIPTION_MAX = 80;
export const CARD_DESIGNS_MAX = 40;
export const CARD_RULES_MAX = 30;
/** A tag: lower case letters, digits, dashes and underscores, as `normaliseTag` makes it. */
export const TAG = /^[a-z0-9][a-z0-9_-]{0,29}$/;
const ID = /^[a-z0-9_-]{1,40}$/i;

/** Pale ink (a dark card) or dark ink (a pale card): what the hairline, the glint and the plain logo treatment follow. */
export const inkIsPale = (d: Pick<CardDesign, 'ink'>): boolean => contrastRatio(d.ink, '#000000') > contrastRatio(d.ink, '#ffffff');

export interface CardDesign {
  id: string;
  name: string;
  /** One line for the admin's list ("Deep emerald and black, gold flecks"). */
  description: string;
  /** Two or three gradient stops, #rrggbb, drawn top-left to bottom-right. */
  colours: string[];
  /** The text colour: the numbers and the name (and the logo and badge with the "ink" treatment). */
  ink: string;
  /** The layers (Saif, 24 Oct 2026 evening): a pattern over the colours, the finish, the rim, the logo and badge, and what moves. */
  pattern: CardPattern;
  finish: CardFinish;
  rim: CardRim;
  logo: CardLogo;
  animation: { kind: CardAnimation; intensity: CardIntensity };
}
export interface CardRule {
  id: string;
  when: { tag: string };
  /** A design id. */
  design: string;
  enabled: boolean;
}
export interface CardDesignsSetting {
  designs: CardDesign[];
  /** In order: the first enabled rule whose tag the customer carries wins. */
  rules: CardRule[];
  /** The design everyone else gets (and the join page shows). It can't be deleted. */
  defaultId: string;
}

const seed = (id: string, name: string, description: string, colours: string[], ink: string, pattern: CardPattern, finish: CardFinish, rim: CardRim, logo: CardLogo, kind: CardAnimation, intensity: CardIntensity = 'normal'): CardDesign =>
  ({ id, name, description, colours, ink, pattern, finish, rim, logo, animation: { kind, intensity } });

/**
 * The fifteen starting designs (Saif, 24 Oct 2026: "they all should look really fancy"), each with its own layers and
 * movement. Amber is today's brand card. Every ink is checked against its stops in the tests, so a seed can never fail its
 * own guard rail; the colours were tuned by eye against it.
 */
export const DEFAULT_CARD_DESIGNS: CardDesign[] = [
  seed('amber', 'Amber', 'Today’s brand card: warm amber, a dark hairline, the sheen', ['#ffe680', '#ffd84d', '#f2b705'], '#141210', 'none', 'satin', 'hairline', 'ink', 'sheen'),
  seed('founders', 'Founders', 'Deep emerald to black, a gold hairline, gold logo, slow gold flecks', ['#124f3c', '#0b2f26', '#071a14'], '#e9c97a', 'grain', 'satin', 'gold', 'gold', 'flecks'),
  seed('gold', 'Tikkas Gold', 'Champagne to bronze, brushed metal, a metallic sheen', ['#f6e7c3', '#dcb978', '#b98a45'], '#2a1d0c', 'brushed', 'metallic', 'hairline', 'ink', 'sheen', 'bold'),
  seed('creator', 'Creator', 'Midnight to violet, gloss, a holographic drift that follows the tilt', ['#101c52', '#2a1f70', '#5b2e93'], '#f3eeff', 'none', 'gloss', 'hairline', 'white', 'holographic'),
  seed('graphite', 'Graphite', 'Black to graphite, satin, a breathing glow at the edges', ['#15151a', '#2a2a30', '#45454d'], '#ffffff', 'none', 'satin', 'hairline', 'white', 'breathe'),
  seed('obsidian', 'Obsidian', 'Near-black gloss with a specular highlight and a soft glow', ['#0a0a0c', '#16161a', '#26262c'], '#f2f0ec', 'glow', 'gloss', 'none', 'white', 'sheen', 'subtle'),
  seed('oxblood', 'Oxblood', 'Deep claret to black, matte, engraved guilloché rings', ['#5a0f1a', '#3a0810', '#140305'], '#f3dcd6', 'guilloche', 'matte', 'hairline', 'white', 'none'),
  seed('royal', 'Royal', 'Deep indigo to sapphire, gloss, a double hairline, a fine lattice', ['#1b1f6b', '#182a8c', '#1f4fb3'], '#eef0ff', 'geometric', 'gloss', 'double', 'white', 'sheen'),
  seed('forest', 'Forest', 'Pine to moss, fine grain, a slow pass of light', ['#143d2b', '#1f5a3a', '#2f6a33'], '#edf5e3', 'grain', 'satin', 'hairline', 'white', 'sheen', 'subtle'),
  seed('rosegold', 'Rose Gold', 'Blush to copper, brushed, metallic, liquid light', ['#f6d8d0', '#dba593', '#c27a62'], '#2a100a', 'brushed', 'metallic', 'hairline', 'ink', 'liquid'),
  seed('pearl', 'Pearl', 'Ivory to pale silver, gloss, a faint iridescent drift', ['#fbf8f2', '#eeeae4', '#d2d3da'], '#2b2a33', 'glow', 'gloss', 'hairline', 'ink', 'holographic', 'subtle'),
  seed('saffron', 'Saffron', 'Deep saffron to burnt orange, satin, a breathing glow', ['#f4a61d', '#e2760f', '#d1620f'], '#1f0c00', 'none', 'satin', 'hairline', 'ink', 'breathe'),
  seed('slate', 'Slate', 'Slate blue to charcoal, brushed metal, the pattern shifts with the tilt', ['#4a5a73', '#33404f', '#1e252e'], '#f0f3f7', 'brushed', 'metallic', 'hairline', 'white', 'parallax'),
  seed('midnightgold', 'Midnight Gold', 'Black with a gold rim, a gold logo and an embossed skewer; a slow twinkle', ['#121212', '#1b1a18', '#2a2622'], '#e9c97a', 'watermark', 'satin', 'gold', 'gold', 'twinkle'),
  seed('ice', 'Ice', 'Pale glacier blue to white, gloss, a cool sheen, dark ink', ['#dbeef9', '#f6fbff', '#ffffff'], '#0f2233', 'glow', 'gloss', 'hairline', 'ink', 'sheen'),
];
/** The starting rules, off until Saif switches them on: the club, creators, the gold level, and a "vip" tag staff give. */
export const DEFAULT_CARD_RULES: CardRule[] = [
  { id: 'rule_founder', when: { tag: 'founder' }, design: 'founders', enabled: false },
  { id: 'rule_creator', when: { tag: 'creator' }, design: 'creator', enabled: false },
  { id: 'rule_gold', when: { tag: 'gold' }, design: 'gold', enabled: false },
  { id: 'rule_vip', when: { tag: 'vip' }, design: 'midnightgold', enabled: false },
];
export const DEFAULT_CARD_DESIGNS_SETTING: CardDesignsSetting = { designs: DEFAULT_CARD_DESIGNS, rules: DEFAULT_CARD_RULES, defaultId: 'amber' };

/** Free text as a tag: lower case, spaces and odd characters to dashes, 30 characters at most; null when nothing is left. */
export function normaliseTag(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.toLowerCase().trim().replace(/[^a-z0-9_-]+/g, '-').replace(/^[-_]+|[-_]+$/g, '').slice(0, 30);
  return TAG.test(t) ? t : null;
}

/** One stop a design's ink can't be read on (stop 1..3), or stop 0: the badge's text (the key colour) on the logo colour. */
export interface DesignContrastProblem { designId: string; designName: string; stop: number; ink: string; colour: string; ratio: number; needs: number }

/** The design's middle stop: the one flat colour that stands for it (the Wallet pass, a swatch, the badge's text). */
export const designKeyColour = (d: Pick<CardDesign, 'colours'>): string => d.colours[Math.floor((d.colours.length - 1) / 2)] ?? d.colours[0]!;

/** The colour the logo and the badge's fill take under the design's treatment. */
export function logoColour(d: Pick<CardDesign, 'ink' | 'logo'>): string {
  return d.logo === 'white' ? '#ffffff' : d.logo === 'gold' ? CARD_GOLD : d.ink;
}

/** Every stop the ink fails on, and the badge when its text can't be read on its fill (empty when the design may be saved). */
export function designContrastProblems(d: Pick<CardDesign, 'id' | 'name' | 'colours' | 'ink'> & Partial<Pick<CardDesign, 'logo'>>): DesignContrastProblem[] {
  const out = d.colours.flatMap((colour, i) => {
    const ratio = contrastRatio(d.ink, colour);
    return ratio < MIN_CONTRAST ? [{ designId: d.id, designName: d.name, stop: i + 1, ink: d.ink, colour, ratio, needs: MIN_CONTRAST }] : [];
  });
  if (d.logo === 'white' || d.logo === 'gold') {
    const fill = logoColour({ ink: d.ink, logo: d.logo }), text = designKeyColour(d);
    const ratio = contrastRatio(text, fill);
    if (ratio < MIN_CONTRAST) out.push({ designId: d.id, designName: d.name, stop: 0, ink: text, colour: fill, ratio, needs: MIN_CONTRAST });
  }
  return out;
}

export function designContrastMessage(p: readonly DesignContrastProblem[]): string | null {
  const first = p[0];
  if (!first) return null;
  const more = p.length > 1 ? ` (and ${p.length - 1} more)` : '';
  if (first.stop === 0) return `“${first.designName}”: the badge’s text is ${ratioText(first.ratio)} on the ${first.colour === CARD_GOLD ? 'gold' : 'white'} badge; it needs 4.5:1${more}.`;
  return `“${first.designName}”: the text is ${ratioText(first.ratio)} on colour ${first.stop}; it needs 4.5:1${more}.`;
}

const oneOf = <T extends string>(list: readonly T[], v: unknown): v is T => (list as readonly unknown[]).includes(v);

function readDesign(raw: unknown, i: number): CardDesign {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const id = typeof o.id === 'string' && ID.test(o.id) ? o.id.toLowerCase() : null;
  if (!id) throw new AppearanceError(`Design ${i + 1} needs an id`, 'bad_card_design');
  const name = typeof o.name === 'string' ? o.name.replace(/\s+/g, ' ').trim() : '';
  if (!name) throw new AppearanceError(`Design ${i + 1} needs a name`, 'bad_card_design');
  if (name.length > CARD_DESIGN_NAME_MAX) throw new AppearanceError(`“${name.slice(0, 20)}…”: the name is at most ${CARD_DESIGN_NAME_MAX} characters`, 'bad_card_design');
  const description = typeof o.description === 'string' ? o.description.replace(/\s+/g, ' ').trim() : '';
  if (description.length > CARD_DESIGN_DESCRIPTION_MAX) throw new AppearanceError(`“${name}”: the description is at most ${CARD_DESIGN_DESCRIPTION_MAX} characters`, 'bad_card_design');
  const stops = Array.isArray(o.colours) ? o.colours : [];
  if (stops.length < 2 || stops.length > 3) throw new AppearanceError(`“${name}” needs two or three colours`, 'bad_card_design');
  const colours = stops.map((c, k) => {
    const v = normaliseHex(c);
    if (!v) throw new AppearanceError(`“${name}”: colour ${k + 1} needs a colour like #1a2b3c`, 'bad_colour');
    return v;
  });
  const ink = normaliseHex(o.ink);
  if (!ink) throw new AppearanceError(`“${name}”: the text colour needs a colour like #1a2b3c`, 'bad_colour');
  // The layers: each one of its values when sent; left out (or the first shape's rim light/dark and sheen on/off), the plain choice.
  const rim = o.rim === 'light' || o.rim === 'dark' || o.rim === undefined ? 'hairline' : o.rim;
  if (!oneOf(CARD_RIMS, rim)) throw new AppearanceError(`“${name}”: the rim is none, hairline, gold or double`, 'bad_card_design');
  const pattern = o.pattern === undefined ? 'none' : o.pattern;
  if (!oneOf(CARD_PATTERNS, pattern)) throw new AppearanceError(`“${name}”: the pattern isn’t one of the list`, 'bad_card_design');
  const finish = o.finish === undefined ? 'satin' : o.finish;
  if (!oneOf(CARD_FINISHES, finish)) throw new AppearanceError(`“${name}”: the finish is matte, satin, gloss or metallic`, 'bad_card_design');
  const logo = o.logo === undefined ? (inkIsPale({ ink }) ? 'white' : 'ink') : o.logo;
  if (!oneOf(CARD_LOGOS, logo)) throw new AppearanceError(`“${name}”: the logo is ink, white, gold or debossed`, 'bad_card_design');
  const a = o.animation && typeof o.animation === 'object' ? (o.animation as Record<string, unknown>) : { kind: o.sheen === false ? 'none' : 'sheen', intensity: 'normal' };
  const kind = a.kind === undefined ? 'none' : a.kind;
  if (!oneOf(CARD_ANIMATIONS, kind)) throw new AppearanceError(`“${name}”: the animation isn’t one of the list`, 'bad_card_design');
  const intensity = a.intensity === undefined ? 'normal' : a.intensity;
  if (!oneOf(CARD_INTENSITIES, intensity)) throw new AppearanceError(`“${name}”: the animation’s intensity is subtle, normal or bold`, 'bad_card_design');
  return { id, name, description, colours, ink, pattern, finish, rim, logo, animation: { kind, intensity } };
}

function readRule(raw: unknown, i: number, designs: readonly CardDesign[]): CardRule {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const id = typeof o.id === 'string' && ID.test(o.id) ? o.id : null;
  if (!id) throw new AppearanceError(`Rule ${i + 1} needs an id`, 'bad_card_rule');
  const when = o.when && typeof o.when === 'object' ? (o.when as Record<string, unknown>) : {};
  const tag = normaliseTag(when.tag);
  if (!tag) throw new AppearanceError(`Rule ${i + 1} needs a tag (letters, digits and dashes)`, 'bad_card_rule');
  const design = typeof o.design === 'string' ? o.design.toLowerCase() : '';
  if (!designs.some((d) => d.id === design)) throw new AppearanceError(`Rule ${i + 1} (“${tag}”) points at a design that isn’t in the list`, 'bad_card_rule');
  if (typeof o.enabled !== 'boolean') throw new AppearanceError(`Rule ${i + 1} is on or off`, 'bad_card_rule');
  return { id, when: { tag }, design, enabled: o.enabled };
}

/**
 * The setting to save, checked: every design readable with its ink at 4.5:1 on every stop (refused with every failing stop,
 * code `bad_contrast`), ids unique, every rule pointing at a design in the list, and the default design present.
 */
export function checkCardDesigns(raw: unknown): CardDesignsSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const rawDesigns = Array.isArray(o.designs) ? o.designs : [];
  if (!rawDesigns.length) throw new AppearanceError('Keep at least one design', 'bad_card_design');
  if (rawDesigns.length > CARD_DESIGNS_MAX) throw new AppearanceError(`At most ${CARD_DESIGNS_MAX} designs`, 'bad_card_design');
  const designs = rawDesigns.map(readDesign);
  const ids = new Set<string>();
  for (const d of designs) {
    if (ids.has(d.id)) throw new AppearanceError(`Two designs have the id “${d.id}”`, 'bad_card_design');
    ids.add(d.id);
  }
  const problems = designs.flatMap(designContrastProblems);
  if (problems.length) throw new AppearanceError(designContrastMessage(problems)!, 'bad_contrast', problems.map((p) => ({ mode: 'light' as const, pair: `design:${p.designId}:${p.stop}`, label: `${p.designName}, text on colour ${p.stop}`, fg: p.ink, bg: p.colour, ratio: p.ratio, needs: p.needs })));
  const rawRules = Array.isArray(o.rules) ? o.rules : [];
  if (rawRules.length > CARD_RULES_MAX) throw new AppearanceError(`At most ${CARD_RULES_MAX} rules`, 'bad_card_rule');
  const rules = rawRules.map((r, i) => readRule(r, i, designs));
  const ruleIds = new Set<string>();
  for (const r of rules) {
    if (ruleIds.has(r.id)) throw new AppearanceError(`Two rules have the id “${r.id}”`, 'bad_card_rule');
    ruleIds.add(r.id);
  }
  const defaultId = typeof o.defaultId === 'string' ? o.defaultId.toLowerCase() : '';
  if (!ids.has(defaultId)) throw new AppearanceError('Pick a default design from the list', 'bad_card_design');
  return { designs, rules, defaultId };
}

/** The stored setting, read leniently: a design or rule that can't be read is dropped; nothing readable gives the starting designs. */
export function storedCardDesigns(raw: unknown): CardDesignsSetting {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null;
  if (!o || !Array.isArray(o.designs)) return DEFAULT_CARD_DESIGNS_SETTING;
  const designs: CardDesign[] = [];
  for (const [i, d] of o.designs.slice(0, CARD_DESIGNS_MAX).entries()) {
    try {
      const read = readDesign(d, i);
      if (!designs.some((x) => x.id === read.id) && !designContrastProblems(read).length) designs.push(read);
    } catch {
      /* dropped */
    }
  }
  if (!designs.length) return DEFAULT_CARD_DESIGNS_SETTING;
  const rules: CardRule[] = [];
  for (const [i, r] of (Array.isArray(o.rules) ? o.rules : []).slice(0, CARD_RULES_MAX).entries()) {
    try {
      const read = readRule(r, i, designs);
      if (!rules.some((x) => x.id === read.id)) rules.push(read);
    } catch {
      /* dropped */
    }
  }
  const wanted = typeof o.defaultId === 'string' ? o.defaultId.toLowerCase() : '';
  const defaultId = designs.some((d) => d.id === wanted) ? wanted : designs[0]!.id;
  return { designs, rules, defaultId };
}

/** The default design (the join page's, and everyone the rules don't name). */
export const defaultDesign = (s: CardDesignsSetting): CardDesign => s.designs.find((d) => d.id === s.defaultId) ?? s.designs[0] ?? DEFAULT_CARD_DESIGNS[0]!;

/** The design for a customer with these tags: the first enabled rule whose tag they carry, else the default. */
export function designFor(tags: readonly string[], s: CardDesignsSetting): { design: CardDesign; rule: CardRule | null } {
  const set = new Set(tags);
  for (const r of s.rules) {
    if (!r.enabled || !set.has(r.when.tag)) continue;
    const d = s.designs.find((x) => x.id === r.design);
    if (d) return { design: d, rule: r };
  }
  return { design: defaultDesign(s), rule: null };
}

/**
 * The automatic tags, one vocabulary for the rules: the group when it isn't plain membership (founder, creator, staff) and
 * the level's id when levels are on (member, regular, gold). Staff add more on the customer's card.
 */
export function autoTags(cu: { group: string; levelId: string | null }): string[] {
  const out: string[] = [];
  if (cu.group && cu.group !== 'member') out.push(cu.group);
  if (cu.levelId) out.push(cu.levelId);
  return out;
}
export const allTags = (auto: readonly string[], manual: readonly string[]): string[] => [...new Set([...auto, ...manual])];

/**
 * The Wallet pass colours a design gives (docs/23 §7, docs/26 §2.4): the key colour behind, the ink for values, and for
 * labels the ink softened towards the background where that still reads at 4.5:1 (the ink itself otherwise).
 */
export function passColoursForDesign(d: Pick<CardDesign, 'colours' | 'ink'>): PassColours {
  const background = designKeyColour(d);
  const soft = composite(d.ink, background, 0.78);
  return { background, foreground: d.ink, label: contrastRatio(soft, background) >= MIN_CONTRAST ? soft : d.ink };
}

/** The card's gradient, as CSS: the stops spread evenly, top-left to bottom-right (three stops sit at 0, 45 and 100%, as the brand card did). */
export function designGradient(d: Pick<CardDesign, 'colours'>): string {
  const c = d.colours;
  const stops = c.length === 3 ? [`${c[0]} 0%`, `${c[1]} 45%`, `${c[2]} 100%`] : c.map((x, i) => `${x} ${Math.round((i / Math.max(1, c.length - 1)) * 100)}%`);
  return `linear-gradient(140deg, ${stops.join(', ')})`;
}

/* ---------- drawing a design: the same variables, attributes and layers in the app and the admin's preview ---------- */

/**
 * The design as CSS variables on the card element (CARD_CSS and the app's .bankcard read them): the gradient, the ink
 * (scoped as --on-yellow so everything inside the card follows), the key colour, the logo colour, the rim, the glint
 * and the animation's intensity. `logoUrl` is the logo image the masked logo takes.
 */
export function designVars(d: CardDesign, logoUrl?: string): Record<string, string> {
  const pale = inkIsPale(d);
  const key = designKeyColour(d);
  const logo = logoColour(d);
  const rim = d.rim === 'none' ? 'transparent' : d.rim === 'gold' ? `color-mix(in srgb, ${CARD_GOLD} 80%, transparent)` : pale ? 'rgba(255,255,255,0.38)' : 'rgba(20,18,16,0.14)';
  const rimInner = d.rim === 'none' ? 'transparent' : d.rim === 'gold' ? `color-mix(in srgb, ${CARD_GOLD} 32%, transparent)` : pale ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.35)';
  const glint = d.finish === 'matte' ? '0' : d.finish === 'gloss' ? (pale ? '0.22' : '0.5') : pale ? '0.14' : '0.36';
  return {
    '--card-bg': designGradient(d),
    '--on-yellow': d.ink,
    '--on-yellow-soft': `color-mix(in srgb, ${d.ink} 80%, ${key})`,
    '--card-key': key,
    '--card-logo': logo,
    '--card-badge-text': key,
    '--card-gold': CARD_GOLD,
    '--card-rim': rim,
    '--card-rim-inner': rimInner,
    '--card-glint': glint,
    '--card-menu-bg': pale ? 'rgba(255,255,255,0.14)' : 'rgba(20,18,16,0.12)',
    '--fx-i': String(INTENSITY_SCALE[d.animation.intensity]),
    ...(logoUrl ? { '--logo-url': `url("${logoUrl}")` } : {}),
  };
}

/** The data attributes CARD_CSS keys its layers on. */
export function designAttrs(d: CardDesign): Record<string, string> {
  return {
    'data-design': d.id,
    'data-pattern': d.pattern,
    'data-finish': d.finish,
    'data-rim': d.rim,
    'data-logo': d.logo,
    'data-fx': d.animation.kind,
    ...(BUSY_PATTERNS.includes(d.pattern) ? { 'data-busy': '' } : {}),
  };
}

/** The layer elements to put first inside the card (class names, in order): the pattern, the finish, then at most two moving layers. */
export function designLayers(d: CardDesign): string[] {
  const out: string[] = [];
  if (d.pattern !== 'none') out.push('bc-layer bc-pattern');
  if (d.finish === 'gloss' || d.finish === 'metallic') out.push('bc-layer bc-finish');
  const k = d.animation.kind;
  if (k !== 'none' && k !== 'parallax') out.push('bc-layer bc-fx bc-fx-a');
  if (k === 'flecks' || k === 'twinkle') out.push('bc-layer bc-fx bc-fx-b');
  return out;
}
